import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { NewsItem } from '../models/news';
import { FileItem } from '../models/file';
import { TeamMember } from '../models/team-member';
import { Verein } from '../models/verein';
import { ShootingSchedule } from '../../features/shooting-schedule/models/shooting-schedule.model';
import { CompetitionDate } from '../../features/competition-dates/models/competition-dates.model';

@Injectable({
  providedIn: 'root'
})
export class Data {
  // Development: serve from public/data via Angular dev server
  // Production: serve from backend via /data-api/data
  private baseUrl = window.location.hostname === 'localhost'
    ? 'data'  // Development: Angular serves public/data
    : '/data-api/data';  // Production: backend serves centralized data

  constructor(private http: HttpClient) {}

  // Load news items
  getNews(): Observable<NewsItem[]> {
    return this.http.get<NewsItem[]>(`${this.baseUrl}/news.json`);
  }

  // Load files by category (generic method)
  getFilesByCategory(jsonFileName: string): Observable<FileItem[]> {
    return this.http.get<FileItem[]>(`${this.baseUrl}/${jsonFileName}`);
  }

  // Legacy methods for backward compatibility
  getErgebnisse(): Observable<FileItem[]> {
    return this.getFilesByCategory('ergebnisse.json');
  }

  getAusschreibungen(): Observable<FileItem[]> {
    return this.getFilesByCategory('ausschreibungen.json');
  }

  getArchiv(): Observable<FileItem[]> {
    return this.getFilesByCategory('archiv.json');
  }

  // Load team members
  getTeam(): Observable<TeamMember[]> {
    return this.http.get<TeamMember[]>(`${this.baseUrl}/team.json`);
  }

  // Load vereine (clubs)
  getVereine(): Observable<Verein[]> {
    return this.http.get<Verein[]>(`${this.baseUrl}/vereine.json`);
  }

  // Load shooting schedule
  getShootingSchedule(): Observable<ShootingSchedule[]> {
    interface AnwesenheitDTO {
      von: string;
      bis: string;
      wechsel: string;
    }

    interface PersonVereinDTO {
      anzahl: string;
      name: string;
      anmerkung: string;
    }

    interface ShootingScheduleDTO {
      anlage: string;
      stand: string;
      datum: string;
      anwesenheit: AnwesenheitDTO;
      personenVerein: PersonVereinDTO[];
      leitung?: string[];
    }

    return this.http.get<ShootingScheduleDTO[]>(`${this.baseUrl}/shooting-schedule.json`).pipe(
      map(schedules => schedules.map(schedule => ({
        ...schedule,
        datum: new Date(schedule.datum)
      })))
    );
  }

  // Load competition dates
  getCompetitionDates(): Observable<CompetitionDate[]> {
    interface DisziplinDTO {
      code: string;
      name: string;
    }

    interface TerminDTO {
      anlage: string;
      datum: string;
      wochentag: string;
      startzeit: string;
      endzeit: string;
      anmerkung: string;
    }

    interface CompetitionDateDTO {
      disziplinen: DisziplinDTO[];
      klassen: string;
      anlagen: string[];
      termine: TerminDTO[];
      anmerkungen: string[];
    }

    return this.http.get<CompetitionDateDTO[]>(`${this.baseUrl}/competition-dates.json`).pipe(
      map(competitions => competitions.map(competition => ({
        ...competition,
        termine: competition.termine.map(termin => ({
          ...termin,
          datum: new Date(termin.datum)
        }))
      })))
    );
  }
}
